// Recently viewed products, newest first, persisted per device. Holds exactly
// the fields ProductCard renders so a rail can be drawn without refetching.
// Not tied to a user: it is browsing history, not account data.
import { defineStore } from 'pinia';

export interface CardProduct {
  id: string;
  name: string;
  platform: string | null;
  ourPriceCents: number;
  totalQty: number;
  imageUrl: string | null;
  coverUrl: string | null;
  isPreorder: boolean;
  available?: boolean;
  genres?: string[] | null;
  ageRating?: string | null;
  offerCount?: number | null;
}

export const RECENT_KEY = 'gamzor.recent.v1';
export const RECENT_MAX = 12;

function isCardProduct(x: unknown): x is CardProduct {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.name === 'string' && typeof o.ourPriceCents === 'number';
}

function read(): CardProduct[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isCardProduct).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function write(items: CardProduct[]) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(items));
  } catch {
    // private mode / quota: history just lives in memory
  }
}

export const useRecentlyViewedStore = defineStore('recently-viewed', {
  state: () => ({ items: read() }),
  actions: {
    /** Move (or add) a product to the front. Picks only card fields so a full
     * product payload never bloats localStorage. */
    push(p: CardProduct) {
      const card: CardProduct = {
        id: p.id,
        name: p.name,
        platform: p.platform,
        ourPriceCents: p.ourPriceCents,
        totalQty: p.totalQty,
        imageUrl: p.imageUrl,
        coverUrl: p.coverUrl,
        isPreorder: p.isPreorder,
        available: p.available,
        genres: p.genres,
        ageRating: p.ageRating,
        offerCount: p.offerCount,
      };
      this.items = [card, ...this.items.filter((i) => i.id !== card.id)].slice(0, RECENT_MAX);
      write(this.items);
    },
  },
});
